import React, { useState, useEffect } from 'react';
import { BottomSheet, StatusActionButton, AddressSuggest } from '../../../shared/ui';
import { sitesV2Api, SiteV2 } from '../../../api/sitesV2Api';
import './EditSiteModal.css';

interface EditSiteModalProps {
  isOpen: boolean;
  site: SiteV2 | null;
  siteId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const EditSiteModal: React.FC<EditSiteModalProps> = ({
  isOpen,
  site,
  siteId,
  onClose,
  onSuccess
}) => {
  const [name, setName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [objectAddress, setObjectAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (site) {
      setName(site.name);
      setCustomerName(site.customerName ?? '');
      setObjectAddress(site.objectAddress ?? '');
    }
  }, [site, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!site) return;
    if (!name.trim()) {
      setError('Название объекта обязательно');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await sitesV2Api.updateSite(siteId, {
        name: name.trim(),
        closed: site.closed,
        customerName: customerName.trim() || undefined,
        objectAddress: objectAddress.trim() || undefined,
      });
      sitesV2Api.clearSiteCache();
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Ошибка сохранения объекта:', err);
      const errorMessage = err.response?.data?.message
        || err.response?.data
        || err.message
        || 'Ошибка сохранения';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Редактировать объект"
      size="auto"
      showHandle
      allowSwipeToClose
      allowSwipeToExpand
    >
      <form onSubmit={handleSubmit} className="edit-site-form">
        {error && (
          <div className="error-message">
            <i className="ri-error-warning-line" aria-hidden />
            <span>{error}</span>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="edit-site-name">Название объекта *</label>
          <input
            id="edit-site-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input"
            placeholder="Введите название объекта..."
            disabled={loading}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="edit-site-customer">Заказчик</label>
          <input
            id="edit-site-customer"
            type="text"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="form-input"
            placeholder="Имя заказчика"
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="edit-site-address">Адрес объекта</label>
          <AddressSuggest
            id="edit-site-address"
            value={objectAddress}
            onChange={setObjectAddress}
            placeholder="Город, улица, дом, квартира..."
            disabled={loading}
            className="edit-site-address-input"
          />
          {!process.env.REACT_APP_DADATA_TOKEN && (
            <small className="form-hint">
              Для подсказок адресов задайте REACT_APP_DADATA_TOKEN (DaData).
            </small>
          )}
        </div>

        <div className="form-actions">
          <StatusActionButton
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Отмена
          </StatusActionButton>
          <StatusActionButton
            type="submit"
            variant="primary"
            disabled={loading || !name.trim()}
            loading={loading}
          >
            Сохранить
          </StatusActionButton>
        </div>
      </form>
    </BottomSheet>
  );
};

export default EditSiteModal;
